import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { aiChatChartInkColors, aiChatChartSeriesColors, chartColors } from 'constants/colors';

import { CHART_HEIGHT, CHART_LINE_WIDTH, CHART_POINT_RADIUS } from '../constants/charts';

import type { ChartData, ChartOptions } from 'chart.js';
import type { ReactElement } from 'react';
import type IAiChatLineChart from '../interfaces/IAiChatLineChart';

import styles from '../sass/AiChat.module.scss';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

/**
 * A line chart of a stored result: one thin line per series, in the palette's fixed order, over
 * one value axis. The legend names the lines and a crosshair tooltip reads every line at a point,
 * so no value has to be guessed from the picture. A gap in a series is a gap in its line.
 */
function AiChatLineChart({ data, xLabel, yLabel }: IAiChatLineChart): ReactElement {
  const chartData: ChartData<'line'> = useMemo(() => ({
    labels: data.labels,
    datasets: data.series.map((series, index) => ({
      label: series.name,
      data: series.values,
      borderColor: aiChatChartSeriesColors[index],
      backgroundColor: aiChatChartSeriesColors[index],
      pointBackgroundColor: aiChatChartSeriesColors[index],
      pointBorderColor: aiChatChartInkColors.surface,
      borderWidth: CHART_LINE_WIDTH,
      pointRadius: CHART_POINT_RADIUS,
      pointHoverRadius: CHART_POINT_RADIUS + 2,
      tension: 0,
      spanGaps: false,
    })),
  }), [data]);

  const options: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      // One line needs no legend: the title names it.
      legend: {
        display: data.series.length > 1,
        position: 'bottom',
        align: 'start',
        labels: {
          usePointStyle: true, boxWidth: 8, padding: 12, color: aiChatChartInkColors.text,
        },
      },
      tooltip: { callbacks: { title: (items) => items[0]?.label || '' } },
    },
    scales: {
      x: {
        grid: { display: false },
        title: { display: !!xLabel, text: xLabel, color: aiChatChartInkColors.text },
        ticks: {
          color: aiChatChartInkColors.text, maxRotation: 45, minRotation: 0, autoSkip: true,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: chartColors.culturedPearl },
        title: { display: !!yLabel, text: yLabel, color: aiChatChartInkColors.text },
        ticks: { color: aiChatChartInkColors.text },
      },
    },
  }), [data.series.length, xLabel, yLabel]);

  return (
    <div className={styles.chart} style={{ height: CHART_HEIGHT }}>
      <Line data={chartData} options={options} />
    </div>
  );
}

export default AiChatLineChart;
